import React from "react";
import { Phone, ChevronDown, Star, ShieldCheck, Clock, BadgeCheck } from "lucide-react";
import { businessInfo, heroImages } from "../data/mockData";

const Hero = () => {
  const handleScroll = (href) => {
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const trustItems = [
    { icon: ShieldCheck, label: "F-Gas & NVQ3 Qualified" },
    { icon: BadgeCheck, label: "Free, No-Obligation Quotes" },
    { icon: Clock, label: "Residential & Commercial" },
  ];

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden bg-brand-charcoal-dark"
    >
      {/* Background Image (real Kold Air install) */}
      {heroImages.mainVideo ? (
        <video
          className="absolute inset-0 w-full h-full object-cover scale-105"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster={heroImages.main}
          aria-hidden="true"
        >
          <source src={heroImages.mainVideo} type="video/mp4" />
        </video>
      ) : (
        <img
          src={heroImages.main}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-105"
          fetchpriority="high"
          decoding="async"
        />
      )}
      {/* Static fallback image layered below (visible if main fails) */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105 -z-10"
        style={{ backgroundImage: `url('${heroImages.mainFallback}')` }}
        aria-hidden="true"
      />
      {/* Layered gradients — grey/silver-tinted for a metallic feel */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0c]/95 via-[#1a1a1d]/72 to-[#2a2a30]/30" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#1f1f23]/50 via-transparent to-[#0a0a0c]/70" />
      {/* Soft silver vignette on the left where text sits */}
      <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-[#3a3a42]/20 via-transparent to-transparent mix-blend-overlay" />

      {/* Subtle grid texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(201,205,211,1) 1px, transparent 1px), linear-gradient(90deg, rgba(201,205,211,1) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-36 pb-24">
        <div className="max-w-3xl">
          {/* White accent line + eyebrow */}
          <div className="flex items-center gap-3 mb-6">
            <span className="block w-10 h-px bg-gradient-to-r from-transparent via-white/70 to-white" />
            <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.32em] text-white/85">
              Cottingham · Hull · Yorkshire
            </span>
          </div>

          {/* Badge row */}
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <div
              className="inline-flex items-center gap-2 bg-gradient-to-b from-white/10 to-white/[0.02] backdrop-blur-md border border-white/20 rounded-full px-4 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]"
              data-testid="hero-gassafe-badge"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-white" />
              <span className="text-white text-[11px] font-bold uppercase tracking-[0.18em]">
                F-Gas & NVQ3 Qualified
              </span>
            </div>
            <div className="inline-flex items-center gap-2 bg-gradient-to-b from-white/10 to-white/[0.02] backdrop-blur-md border border-white/20 rounded-full px-4 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-3 h-3 ${
                      i < Math.round(businessInfo.rating)
                        ? "text-white fill-white"
                        : "text-white/25 fill-white/25"
                    }`}
                  />
                ))}
              </div>
              <span className="text-white/85 text-[11px] font-semibold tracking-wide">
                {businessInfo.rating.toFixed(1)} · {businessInfo.reviewCount} Google Reviews
              </span>
            </div>
          </div>

          {/* Heading — clean white throughout */}
          <h1 className="font-heading text-white leading-[1.05] mb-6">
            <span className="block text-3xl sm:text-4xl lg:text-5xl tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
              Stay Cool.
            </span>
            <span className="block text-3xl sm:text-4xl lg:text-5xl italic font-medium text-white/85 tracking-tight mt-1 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
              Stay Comfortable.
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-sm sm:text-base text-white/75 max-w-xl leading-relaxed font-light mb-9">
            {businessInfo.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-3 mb-12">
            <a
              href={`tel:${businessInfo.phone}`}
              className="group inline-flex items-center gap-2.5 bg-white text-brand-charcoal-dark px-7 py-3.5 rounded-lg text-sm font-bold tracking-wide transition-all duration-200 hover:bg-white/90 hover:shadow-xl hover:shadow-white/20 hover:-translate-y-0.5"
              data-testid="hero-call-btn"
            >
              <Phone className="w-4 h-4" />
              Call {businessInfo.phone}
            </a>
            <button
              onClick={() => handleScroll("#contact")}
              className="inline-flex items-center gap-2 border border-white/30 bg-white/[0.04] backdrop-blur-sm text-white hover:bg-white/10 hover:border-white/60 px-7 py-3.5 rounded-lg text-sm font-bold tracking-wide transition-all duration-200 hover:-translate-y-0.5"
              data-testid="hero-quote-btn"
            >
              Get a Free Quote
            </button>
          </div>

          {/* Trust strip — white divider + white icons */}
          <div className="flex flex-wrap items-center gap-x-7 gap-y-3 pt-7 border-t border-white/15 relative">
            <span className="absolute -top-px left-0 w-24 h-px bg-gradient-to-r from-white to-transparent" />
            {trustItems.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <Icon
                  className="w-4 h-4 text-white/85"
                  strokeWidth={1.75}
                />
                <span className="text-white/70 text-xs font-semibold uppercase tracking-[0.12em]">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Down Indicator */}
      <button
        onClick={() => handleScroll("#about")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/35 hover:text-white/80 transition-colors animate-bounce"
        aria-label="Scroll down"
      >
        <ChevronDown className="w-6 h-6" />
      </button>
    </section>
  );
};

export default Hero;
